/**
 * /well-servicing/tickets/:id — one Well Servicing field ticket (fieldiq-api#1294,
 * WS-4). Well Testing's ticket screen, carried over: this vertical's API
 * mirrors WT-4. The ticket names its rig and lists the tour sheets it covers,
 * each a link to the sheet.
 *
 * WHAT MAY BE EDITED (wsTicketEditMode, mirroring the API):
 *   draft              lines and header edit freely.
 *   office_approved,   an edit returns the ticket to draft. The screen does
 *   customer_rejected  not offer the edit until the office says "unlock" to a
 *                      sentence saying so; when a write then comes back
 *                      `reverted_to_draft: true` the screen says that too.
 *   customer_approved  frozen. Only the internal notes change.
 *   void               frozen entirely, notes included; it says who voided it
 *                      and why. Void is offered from every other status, with
 *                      a required reason, and is confirmed inline.
 *
 * Refresh from the tour sheets redraws the 'auto' lines and says FIRST that an
 * office price on one of them is lost (office lines are kept). The PDF is
 * offered once the office has signed (a draft or void ticket is 409). No
 * modals: every confirm is inline and every refusal is a sentence in the
 * refusal style.
 *
 * Write controls (refresh, lines, details, approve, send, record, void,
 * delete) are drawn for WS_TICKET_WRITE_ROLES only — the API refuses every
 * other role; a supervisor or dispatcher reads the ticket.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Ban, FileText, Lock, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { canUseWsAi, wsTicketPreflightable } from '../../../lib/ws-ai';
import { WsAiLauncher } from '../ai/WsAiBits';
import { useWsMeta } from '../../../hooks/useWellServicing';
import { useDeleteWsTicket, useWsRates, useWsTicket, useRefreshWsTicket, useUpdateWsTicket, useVoidWsTicket, type WsTicketEditResult } from '../../../hooks/useWsTickets';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import {
  canWriteWsTickets,
  wsScopeText,
  wsTicketDeletable,
  wsTicketEditMode,
  wsTicketHasPdf,
  wsTicketInvoicedFrom,
  wsTicketRefusal,
  wsTicketStatusSkin,
  wsTicketWellText,
  WS_REFRESH_WARNING,
  WS_VOID_WARNING,
} from '../../../lib/ws-tickets';
import { blankToNull, wsDay, wsTourStatusSkin } from '../../../lib/well-servicing';
import type { WsTicket, WsTicketCatalog, WsTicketHeaderWrite } from '../../../types/ws-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WS_EYEBROW, WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsBadge, WsEmpty, WsField, WsFormError, WsLoading, WsPanel, WsSpec } from '../WsBits';
import { PfInlineConfirm as WsInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { WsRevertedNotice } from './TicketBits';
import { WsTicketLines } from './TicketLines';
import { WsTicketSignOff } from './TicketSignOff';
import { WsTicketInvoiceLink } from '../invoices/InvoiceBits';

const HEADER_FIELDS: { key: keyof WsTicketHeaderWrite; read: keyof WsTicket; label: string; max: number }[] = [
  { key: 'afe_number', read: 'afeNumber', label: 'AFE number', max: 100 },
  { key: 'po_number', read: 'poNumber', label: 'PO number', max: 255 },
  { key: 'cost_code', read: 'costCode', label: 'Cost code', max: 100 },
];

function HeaderForm({ ticket, pending, error, onCancel, onSubmit }: {
  ticket: WsTicket;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: WsTicketHeaderWrite) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = { customer_notes: ticket.customerNotes ?? '' };
    HEADER_FIELDS.forEach((f) => { v[f.key] = (ticket[f.read] as string | null) ?? ''; });
    return v;
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValues((v) => ({ ...v, [k]: e.target.value }));
  return (
    <div data-testid="ws-ticket-header-form" className="space-y-3 border-t border-fieldiq-border px-4 py-4">
      <WsFormError message={error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {HEADER_FIELDS.map((f) => (
          <WsField key={f.key} label={f.label}>
            <input className={WS_INPUT} maxLength={f.max} value={values[f.key]} onChange={set(f.key)} />
          </WsField>
        ))}
      </div>
      <WsField label="Notes for the customer" hint="Printed on the ticket.">
        <textarea className={`${WS_INPUT} h-24 py-2`} maxLength={5000} value={values.customer_notes} onChange={set('customer_notes')} />
      </WsField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WS_SECONDARY} onClick={onCancel} disabled={pending}>Cancel</button>
        <button
          type="button"
          className={WS_PRIMARY}
          disabled={pending}
          onClick={() => {
            const body: WsTicketHeaderWrite = {};
            const cur: Record<string, string | null> = { customer_notes: ticket.customerNotes };
            HEADER_FIELDS.forEach((f) => { cur[f.key] = ticket[f.read] as string | null; });
            Object.keys(values).forEach((k) => {
              const next = blankToNull(values[k]);
              if (next !== (cur[k] ?? null)) (body as Record<string, string | null>)[k] = next;
            });
            onSubmit(body);
          }}
        >
          {pending ? 'Saving…' : 'Save details'}
        </button>
      </div>
    </div>
  );
}

/**
 * Void: a required reason, said before the office presses it — the button
 * refuses an empty reason itself, and a 400 `reason_required` still reads as a sentence.
 * A ticket on a live invoice is refused (409 `ticket_invoiced`, WS-5): the sentence names the invoice and
 * the page links to it, since the invoice is what has to be voided first.
 */
function VoidTicket({ ticket, onCancel, onDone }: { ticket: WsTicket; onCancel: () => void; onDone: () => void }) {
  const voidTicket = useVoidWsTicket();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [onInvoice, setOnInvoice] = useState<{ id: string; number: string } | null>(null);
  return (
    <div data-testid="ws-ticket-void" role="group" aria-label="Void this ticket" className="space-y-3 rounded-fi-control border border-fi-red/50 bg-fi-signal-red/10 px-4 py-4">
      <p className="text-sm text-fieldiq-text">{WS_VOID_WARNING}</p>
      <WsFormError message={error} />
      {onInvoice && (
        <p className="text-sm text-fieldiq-text" data-testid="ws-ticket-void-invoice">
          Open{' '}
          <Link className="font-mono font-bold underline" to={`/well-servicing/invoices/${onInvoice.id}`}>
            {onInvoice.number}
          </Link>{' '}
          to void it.
        </p>
      )}
      <WsField label="Why is it being voided? *">
        <textarea className={`${WS_INPUT} h-20 py-2`} maxLength={2000} aria-label="Reason for voiding" value={reason} onChange={(e) => setReason(e.target.value)} />
      </WsField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WS_SECONDARY} onClick={onCancel} disabled={voidTicket.isPending}>Keep it</button>
        <button
          type="button"
          className={WS_PRIMARY}
          disabled={voidTicket.isPending}
          onClick={() => {
            setError(null);
            setOnInvoice(null);
            if (!reason.trim()) return setError('Say why the ticket is being voided.');
            voidTicket.mutate(
              { id: ticket.id, reason: reason.trim() },
              {
                onSuccess: () => { toast.success(`${ticket.ticketNumber} voided`); onDone(); },
                onError: (e) => {
                  setError(wsTicketRefusal(e, 'The ticket could not be voided.'));
                  setOnInvoice(wsTicketInvoicedFrom(e));
                },
              },
            );
          }}
        >
          {voidTicket.isPending ? 'Voiding…' : 'Void ticket'}
        </button>
      </div>
    </div>
  );
}

function InternalNotes({ ticket, canWrite }: { ticket: WsTicket; canWrite: boolean }) {
  const update = useUpdateWsTicket();
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(ticket.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <WsPanel
      eyebrow="Internal"
      title="Office notes"
      testId="ws-ticket-notes"
      actions={canWrite && ticket.status !== 'void' && !editing ? <button type="button" className={WS_SECONDARY} onClick={() => { setNotes(ticket.notes ?? ''); setError(null); setEditing(true); }}>Edit notes</button> : undefined}
    >
      {editing ? (
        <div className="space-y-3 px-4 py-4">
          <WsFormError message={error} />
          <WsField label="Notes" hint="Never printed on the ticket, so they can change at any status but void.">
            <textarea className={`${WS_INPUT} h-24 py-2`} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </WsField>
          <div className="flex justify-end gap-2">
            <button type="button" className={WS_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>Cancel</button>
            <button
              type="button"
              className={WS_PRIMARY}
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: ticket.id, body: { notes: blankToNull(notes) } },
                  { onSuccess: () => { toast.success('Notes saved'); setEditing(false); }, onError: (e) => setError(wsTicketRefusal(e, 'The notes could not be saved.')) },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap px-4 py-4 text-sm text-fieldiq-text">{ticket.notes || '—'}</p>
      )}
    </WsPanel>
  );
}

/** The tour sheets this ticket covers (still linked), oldest first — the API's `tours`. */
const liveTours = (t: WsTicket) => (t.tours ?? []).filter((x) => !x.releasedAt);

/** `12 Oct 2026` or `12 Oct 2026 – 15 Oct 2026` from the linked sheets; `—` when it covers none. */
export function tourDays(t: WsTicket): string {
  if (t.scope === 'day' && t.workDate) return wsDay(t.workDate);
  const days = liveTours(t).map((x) => x.workDate).filter((d): d is string => !!d).sort();
  if (!days.length) return '—';
  return days[0] === days[days.length - 1] ? wsDay(days[0]) : `${wsDay(days[0])} – ${wsDay(days[days.length - 1])}`;
}

function TicketTours({ ticket }: { ticket: WsTicket }) {
  const tours = liveTours(ticket);
  return (
    <div data-testid="ws-ticket-tours" className="border-t border-fieldiq-border px-4 py-3">
      <p className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Tour sheets on this ticket</p>
      {!tours.length ? (
        <p className="text-sm text-fieldiq-muted">—</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {tours.map((x) => (
            <li key={x.id}>
              <Link className="inline-flex items-center gap-2 rounded-fi-control border border-fieldiq-border px-2 py-1 text-sm text-fieldiq-text underline" to={`/well-servicing/tours/${x.id}`}>
                {wsDay(x.workDate)}
                <WsBadge skin={wsTourStatusSkin(x.status)} value={x.status ?? ''} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function WsTicketDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/well-servicing/tickets');
  const { user } = useAuth();
  const canWrite = canWriteWsTickets(user?.role);
  const query = useWsTicket(id);
  const catalog = (useWsMeta().data?.data ?? null) as WsTicketCatalog | null;
  const ratesQuery = useWsRates(undefined, canWrite);
  const update = useUpdateWsTicket();
  const refresh = useRefreshWsTicket();
  const del = useDeleteWsTicket();
  const [reverted, setReverted] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [unpricedIds, setUnpricedIds] = useState<string[]>([]);
  const [voiding, setVoiding] = useState(false);
  const t = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const tz = meta?.timeZone ?? null;
  const failure = queryLoadFailure(query);
  const mode = wsTicketEditMode(t?.status);
  const editable = canWrite && (mode === 'free' || (mode === 'reverts' && unlocked));

  const onEdited = (r: WsTicketEditResult) => {
    if (r.reverted) {
      setReverted(true);
      setUnlocked(false);
    }
  };

  const openPdf = async () => {
    if (!t) return;
    setPdfError(null);
    const win = claimBlobTab();
    if (!win) return setPdfError(POPUP_BLOCKED_MESSAGE);
    setPdfBusy(true);
    try {
      const res = await api.wellServicing.tickets.pdf(t.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setPdfError(await getBlobApiErrorMessage(e, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div data-testid="ws-ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title={t?.ticketNumber || (query.isLoading ? 'Field ticket' : '—')}
        description={t ? `${t.customerName ?? '—'} · ${wsTicketWellText(t)} · ${t.jobNumber ?? '—'} · ${wsScopeText(t)}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          t ? (
            <div className="flex flex-wrap gap-2">
              {wsTicketHasPdf(t.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy}>
                  <FileText className="size-4" aria-hidden="true" />
                  {pdfBusy ? 'Opening…' : 'PDF'}
                </button>
              )}
              {canWrite && t.status !== 'void' && (
                <button type="button" className={DH_BAND_SECONDARY} data-testid="ws-ticket-void-open" onClick={() => setVoiding(true)} disabled={voiding}>
                  <Ban className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
              {canWrite && wsTicketDeletable(t.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WsLoading label="Loading ticket" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !t) ? (
            <WsPanel eyebrow="Field ticket" title="Field ticket">
              <WsEmpty>This ticket was not found. It may have been deleted.</WsEmpty>
            </WsPanel>
          ) : failure || !t ? (
            <WsPanel eyebrow="Field ticket" title="Field ticket">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this ticket" />
            </WsPanel>
          ) : (
            <>
              <WsFormError message={pdfError} />
              {voiding && canWrite && t.status !== 'void' && <VoidTicket ticket={t} onCancel={() => setVoiding(false)} onDone={() => setVoiding(false)} />}
              {reverted && <WsRevertedNotice onDismiss={() => setReverted(false)} />}
              {deleting && (
                <WsInlineConfirm
                  testId="ws-ticket-delete-confirm"
                  message={`Delete ${t.ticketNumber}? Its lines go with it. The job's tour sheets are not touched, so it can be drafted again.`}
                  confirmLabel="Delete ticket"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(t.id, {
                      onSuccess: () => {
                        toast.success(`${t.ticketNumber} deleted`);
                        navigate('/well-servicing/tickets', { replace: true });
                      },
                      onError: (e) => setDeleteError(wsTicketRefusal(e, 'The ticket could not be deleted.')),
                    })
                  }
                />
              )}
              {canWrite && mode === 'reverts' && !unlocked && (
                <div className="flex flex-wrap items-center gap-3">
                  {unlocking ? (
                    <WsInlineConfirm
                      testId="ws-ticket-unlock-confirm"
                      message="Editing this ticket returns it to draft: the office approval is withdrawn, and any review link sent to the customer stops working. It has to be approved and sent again."
                      confirmLabel="Unlock to edit"
                      cancelLabel="Leave it signed"
                      onCancel={() => setUnlocking(false)}
                      onConfirm={() => { setUnlocked(true); setUnlocking(false); }}
                    />
                  ) : (
                    <button type="button" className={WS_SECONDARY} onClick={() => setUnlocking(true)} data-testid="ws-ticket-unlock">
                      <Lock className="size-4" aria-hidden="true" />
                      Edit this ticket
                    </button>
                  )}
                </div>
              )}
              {mode === 'frozen' && (
                <p data-testid="ws-ticket-frozen" className="text-sm text-fieldiq-muted">
                  The customer has approved this ticket, so what it bills cannot change. Office notes can still be edited.
                </p>
              )}
              {mode === 'void' && (
                <p data-testid="ws-ticket-voided" role="status" className="rounded-fi-control border border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                  Void{t.voidedByName ? ` — voided by ${t.voidedByName}` : ''}
                  {t.voidedAt ? ` on ${formatInstant(t.voidedAt, tz) ?? '—'}` : ''}. Reason: {t.voidReason || '—'}. A void ticket bills nothing and
                  cannot be changed.
                </p>
              )}
              <WsPanel
                eyebrow="Field ticket"
                title="Details"
                testId="ws-ticket-details"
                actions={editable && !editingHeader ? <button type="button" className={WS_SECONDARY} onClick={() => { setHeaderError(null); setEditingHeader(true); }}>Edit details</button> : undefined}
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WsSpec label="Status" value={<WsBadge skin={wsTicketStatusSkin(t.status)} value={t.status} />} />
                  <WsSpec label="Job" value={t.jobNumber ? <Link className="underline" to={`/well-servicing/jobs/${t.jobId}?tab=tickets`}>{t.jobNumber}</Link> : '—'} mono />
                  <WsSpec label="Customer" value={t.customerName || '—'} />
                  <WsSpec label="Covers" value={wsScopeText(t)} />
                  <WsSpec label="Well" value={wsTicketWellText(t)} />
                  <WsSpec label="Rig" value={t.rigNumber || '—'} mono />
                  <WsSpec label="Days" value={tourDays(t)} />
                  <WsSpec label="Province / state" value={t.provinceState || '—'} />
                  <WsSpec label="Currency" value={t.currency || '—'} />
                  <WsSpec label="Created" value={t.createdAt ? formatInstant(t.createdAt, tz) ?? '—' : '—'} />
                  {HEADER_FIELDS.map((f) => (
                    <WsSpec key={f.key} label={f.label} value={(t[f.read] as string | null) || '—'} />
                  ))}
                </dl>
                {t.customerNotes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{t.customerNotes}</p>}
                <TicketTours ticket={t} />
                {editable && editingHeader && (
                  <HeaderForm
                    ticket={t}
                    pending={update.isPending}
                    error={headerError}
                    onCancel={() => setEditingHeader(false)}
                    onSubmit={(body) => {
                      if (!Object.keys(body).length) return setHeaderError('Nothing has changed.');
                      setHeaderError(null);
                      update.mutate({ id: t.id, body }, {
                        onSuccess: (r) => { toast.success('Details saved'); setEditingHeader(false); onEdited(r); },
                        onError: (e) => setHeaderError(wsTicketRefusal(e, 'The details could not be saved.')),
                      });
                    }}
                  />
                )}
              </WsPanel>
              {editable && (
                <div className="space-y-2">
                  {refreshing ? (
                    <WsInlineConfirm
                      testId="ws-ticket-refresh-confirm"
                      message={WS_REFRESH_WARNING}
                      confirmLabel="Refresh from the tour sheets"
                      cancelLabel="Not now"
                      pending={refresh.isPending}
                      error={refreshError}
                      onCancel={() => setRefreshing(false)}
                      onConfirm={() =>
                        refresh.mutate(t.id, {
                          onSuccess: (r) => { toast.success('Lines redrawn from the tour sheets'); setRefreshing(false); onEdited(r); },
                          onError: (e) => setRefreshError(wsTicketRefusal(e, 'The ticket could not be refreshed.')),
                        })
                      }
                    />
                  ) : (
                    <button type="button" className={WS_SECONDARY} data-testid="ws-ticket-refresh" onClick={() => { setRefreshError(null); setRefreshing(true); }}>
                      <RefreshCw className="size-4" aria-hidden="true" />
                      Refresh from the tour sheets
                    </button>
                  )}
                </div>
              )}
              <WsTicketLines
                ticket={t}
                editable={editable}
                canWrite={canWrite}
                rates={ratesQuery.data?.data ?? []}
                catalog={catalog}
                highlight={unpricedIds}
                onEdited={onEdited}
              />
              {t.status === 'customer_approved' && <WsTicketInvoiceLink ticket={t} />}
              <WsTicketSignOff ticket={t} canWrite={canWrite} tz={tz} today={meta?.today ?? null} onUnpriced={setUnpricedIds} />
              <InternalNotes ticket={t} canWrite={canWrite} />
              {canUseWsAi(user?.role) && wsTicketPreflightable(t.status) && (
                <WsAiLauncher
                  key={t.id}
                  request={{ agent: 'ws_ticket_preflight', ticketId: t.id }}
                  eyebrow="Before sending"
                  title="Ticket preflight"
                  blurb="Checks this ticket for unpriced lines and the job's approved sheets for billing classes, subsistence or rent-ins with no rate. The AI's text explains the flags; it changes nothing."
                  testId="ws-ai-ticket-preflight"
                />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
